/**
 * V2 Category page — the heart of Yoink's Discover leg, dressed as a
 * splash page since 2026-07-13 (user ask: good enough to be a brand's
 * official location map). The hero leads with a photo collage from the
 * category's own yoinks, the big name, stat stamps, and your progress;
 * the map sits right under it, always mounted and framed to every pin
 * (green = yours), and panning it live-filters the cards below.
 *
 * Categories are the primary follow object (decisions log), so
 * Follow-this-category is the headline action in the hero. For a
 * collection (a category with children, V1's "Set"), progress is
 * child-categories-completed — "You've got 12 of 30" — exactly V1's
 * math. The feed below is the category's yoinks.
 *
 * Rendering is split into stable regions (hero / controls / map / body)
 * so store noise and the map's own idle writes never remount the map —
 * the 2026-07-08 blink-loop lesson, now structural.
 */
import * as gateway from '../../core/gateway.js';
import { fetchAllCategories, categorySlug, findCategoryBySlug } from '../../core/categories.js';
import { yoinkCard, thumbUrl, heroPhotos } from '../../ui/card.js';
import { categoryIcon, icon } from '../../ui/icons.js';
import { shareButton, handleShareClick, shareTextFrom } from '../../ui/share.js';
import { shareMenu } from '../../ui/share-menu.js';
import { embedButton, handleEmbedClick, isEmbed } from '../../ui/embed.js';
import { OPEN_AUTH_EVENT } from '../../ui/auth-button.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';
import { track } from '../../core/analytics.js';
import { fetchCategoryLeaderboard, standing, gapToPass, challengeText } from './leaderboard.js';
import { sharePhotosButton, createSharePhotosHandler } from '../../ui/share-photos.js';
import { hashtags } from '../../ui/share-card.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { isFollowingCategory, followCategory, unfollowCategory } from '../follows/follows.js';
import { fetchCategoryLists } from '../lists/lists.js';
import { mountMap, mapsAvailable, rowsInArea, markGeoGranted } from '../../ui/map-view.js';

// Split 2026-07-25: category.js had grown to 998 lines against the plan's
// ~400 budget — the largest file in src/. Reads moved to ./category-data.js
// and the pure set/stats maths to ./category-stats.js. Every name this module
// exported before is re-exported below UNCHANGED; three test files import
// them from here.
import { SUBTREE_CAP, fetchSubtreeYoinks, fetchMyYoinkedCategoryIds, fetchCategoryArticle } from './category-data.js';
import { articleSections, articleParagraphHtml } from '../../core/article.js';
import {
  subtreeIds, computeCategoryStats, haversineKm, uncollectedNearby,
  computeLeaderboard, computeProgress, buildSetTree,
} from './category-stats.js';

export {
  subtreeIds, computeCategoryStats, haversineKm, uncollectedNearby,
  computeLeaderboard, computeProgress, buildSetTree,
};


// The hero-collage photo picker now lives with the card helpers
// (ui/card.js) — the profile splash shares it. Re-exported for tests.
export { heroPhotos };


// The viewport filter now lives with the map (map-view.js) so Discover
// can use it too; re-exported here for existing importers and tests.
export { rowsInArea };

/**
 * Mount the category view.
 * @param {HTMLElement} el
 * @param {object} ctx - { store, router, params: { slug } }
 */
export function mount(el, ctx) {
  const { store, router, params } = ctx;
  let category = null;
  let allCatalog = []; // full catalog (for subtree filters + Set backlink)
  let children = [];
  let progress = null; // { done, total } when signed in and a collection
  let myYoinkedCatIds = null; // for the Set checklist's per-child marks
  let collagePhotos = []; // hero collage: recent photos (rows for feeds, loadCollage for Sets)
  let setPhotos = []; // a Set's subtree photos, for its share cards
  let setActivity = null; // { label } — collection-wide activity, empty pages only
  let collageReady = false; // the Set collage query has settled (or had nothing to fetch)
  let leaderboard = []; // top collectors (distinct places), V1 math
  let catLists = []; // public lists carrying this category (20260813f; [] pre-migration)
  let rows = null; // the whole subtree feed (null = loading)
  let childFilter = null; // in-page sub-category filter (V1 pills)
  let visibleCount = 48;
  let nearby = null; // { state: 'off'|'denied'|'none'|'ready', places }
  // No Grid/Map toggle here since 2026-07-13 (iPad feedback: with the
  // always-on splash map it did nothing but resize the map). The
  // session's viewMode still steers Discover/profile; old ?view= links
  // simply land on the one layout that shows both.
  let mapHandle = null;
  let mapArea = null; // live map viewport — filters the grid below (user report 2026-07-08)
  let destroyed = false;
  let followBusy = false;
  let article = null; // "About" sections (categories.article) — null until loaded / when absent
  let heroEl = null;
  let controlsEl = null;
  let bodyEl = null;
  let aboutEl = null;
  let mapHostEl = null;
  let pinsSig = ''; // skips redundant setYoinks calls (each one re-frames the map)

  // Signed-out tally (2026-07-26 landing-pages push step 4): "how many of
  // these have you been to?" made tappable. A visitor marks the ones they
  // have visited, the bar fills, and the call to action offers to keep the
  // count. Local only (per-category localStorage) — a claim, not a log —
  // and after signup it becomes the "log them to make it count" bridge.
  let tally = new Set();
  let tallyTracked = false; // one analytics event per visit, not per tap

  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  const embedded = isEmbed();

  function tallyKey() {
    return `yoink_tally_${category.id}`;
  }

  function loadTally() {
    try {
      const stored = JSON.parse(localStorage.getItem(tallyKey()) || '[]');
      if (Array.isArray(stored)) tally = new Set(stored);
    } catch { /* private mode / bad JSON — session-only tally */ }
  }

  function saveTally() {
    try {
      localStorage.setItem(tallyKey(), JSON.stringify([...tally]));
    } catch { /* private mode — the taps still count until the page closes */ }
  }

  function followBtn() {
    if (!store.get().user || !category) return '';
    const following = isFollowingCategory(store, category.id);
    return `
      <button type="button" class="v2-btn-follow ${following ? 'is-following' : ''}"
        data-follow-category aria-pressed="${following}" ${followBusy ? 'disabled' : ''}>
        ${followBusy ? '…' : following ? 'Following' : 'Follow'}
      </button>`;
  }

  // Every Collection shows its checklist — user decision 2026-07-12,
  // SUPERSEDING V1's rule (category-panel.js:526), which excluded
  // can_tag=false Sets and dumped them into the aggregated feed. The
  // whole point of opening a collection from your journal is seeing the
  // choices and what's still missing, and structural Sets (Food, tagged
  // only through their children) are exactly the ones that need it.
  function isSetPanel() {
    return category?.is_set === true;
  }

  /**
   * The hero collage: 5+ photos get the mosaic (one feature photo, four
   * fill), 3-4 an even strip, fewer than 3 nothing — a thin collage
   * sells worse than none.
   */
  function collageHtml() {
    if (collagePhotos.length < 3) return '';
    const photos = collagePhotos.slice(0, 5);
    return `
      <div class="cat-hero-collage${photos.length >= 5 ? ' collage-mosaic' : ''}" aria-hidden="true">
        ${photos.map((url, i) => `<img src="${thumbUrl(url)}" alt="" loading="${i < 3 ? 'eager' : 'lazy'}"
          data-fallback-src="${url}">`).join('')}
      </div>`;
  }

  /**
   * V1's stats — N yoinks · M locations worldwide · K new this week —
   * as passport-stamp tiles. The numbers and words are the same text
   * (stamps repeat information, they never solely carry it — art
   * direction rule 3; here they ARE the text).
   */
  function statStampsHtml() {
    if (!rows?.length) return '';
    const stats = computeCategoryStats(rows, new Date());
    return `
      <div class="cat-stats-line" role="group" aria-label="Category totals">
        <span class="cat-stat"><strong>${stats.yoinks}</strong> yoink${stats.yoinks === 1 ? '' : 's'}</span>
        <span class="cat-stat"><strong>${stats.locations}</strong> location${stats.locations === 1 ? '' : 's'} worldwide</span>
        ${stats.newThisWeek ? `<span class="cat-stat cat-stat-fresh"><strong>${stats.newThisWeek}</strong> new this week</span>` : ''}
      </div>
      ${rows.length >= SUBTREE_CAP ? '<p class="log-hint">The largest categories show the newest 2000 yoinks.</p>' : ''}`;
  }

  /** V1's non-Set progress: your distinct locations out of everyone's. */
  function locationsProgressHtml() {
    const me = store.get().user;
    if (!rows?.length) return '';
    if (!me) {
      // Same reasoning as the Set branch: give a first-time visitor an
      // action, not directions to a corner that moves at phone width —
      // and lead with the question every arrival can answer in their
      // head (landing-pages push step 4).
      const logged = new Set(rows.map((r) => r.location_id).filter(Boolean)).size;
      return `<p class="log-hint">${logged
        ? `Explorers here have logged ${logged} place${logged === 1 ? '' : 's'}. How many have you been to?`
        : 'How many of these have you been to?'} Keep count — with the photos to prove it.</p>
        <button type="button" class="v2-btn-leather" data-open-auth>Start collecting</button>`;
    }
    const globalLocs = new Set(rows.map((r) => r.location_id).filter(Boolean));
    const myLocs = new Set(rows.filter((r) => r.user_id === me.id).map((r) => r.location_id).filter(Boolean));
    if (!globalLocs.size) return '';
    const pct = Math.round((myLocs.size / globalLocs.size) * 100);
    return `
      <section class="set-progress" aria-label="Your progress">
        <div class="set-progress-head">
          <h3 class="friends-title">Your progress</h3>
          <span class="set-progress-label">${myLocs.size} of ${globalLocs.size} locations (${pct}%)</span>
        </div>
        <div class="collection-bar set-progress-bar"><span class="collection-fill" style="width:${pct}%"></span></div>
      </section>`;
  }

  /** The splash hero: collage, then the title panel overlapping it. */
  function heroHtml() {
    return `
      <header class="cat-hero">
        ${collageHtml()}
        <div class="cat-hero-panel${collagePhotos.length >= 3 ? ' has-collage' : ''}">
          <div class="v2-category-head">
            <span class="cat-head-icon" aria-hidden="true">${categoryIcon(category, 40)}</span>
            <div class="cat-head-names">
              <h2 class="cat-head-name">${escapeHtml(category.name)}
                ${isSetPanel() ? '<span class="set-badge">Collection</span>' : ''}</h2>
              ${category.description ? `<p class="cat-head-desc">${escapeHtml(category.description)}</p>` : ''}
            </div>
            ${followBtn()}
          </div>
          ${isSetPanel() ? '' : statStampsHtml() + locationsProgressHtml()}
        </div>
      </header>`;
  }

  /**
   * The collection checklist (V1 renderSetPanel, category-panel.js:526-722),
   * as a tree since 2026-07-12: leaves are the choices — ✓ (you've yoinked
   * in it) or ○ (not yet), completed first then alphabetical — and nested
   * groups (Food Type → Street Food → …) expand IN PLACE with a done/total
   * count, instead of navigating away. The progress bar counts leaf
   * choices across the whole subtree and celebrates at 100%.
   */
  /**
   * The collection's size and your progress through it: leaf choices across
   * the whole subtree. Shared with the share card so the card and the bar on
   * the page can never print different numbers.
   */
  function setTotals() {
    const mine = myYoinkedCatIds || new Set();
    const tree = buildSetTree(allCatalog, category.id, mine);
    return {
      tree,
      total: tree.reduce((n, node) => n + node.leaves, 0),
      done: tree.reduce((n, node) => n + node.doneLeaves, 0),
    };
  }

  /** Leaf category ids across the whole tree — what the tally counts over. */
  function treeLeafIds(nodes) {
    return nodes.flatMap((node) => (node.kids.length ? treeLeafIds(node.kids) : [node.cat.id]));
  }

  /** The tally, counting only leaves that are really in this set (stale storage). */
  function tallyCount(tree) {
    const leaves = new Set(treeLeafIds(tree));
    return [...tally].filter((id) => leaves.has(id)).length;
  }

  // The signed-out progress panel's two mutable sentences. Shared between
  // the initial render and the per-tap patch so they can never disagree.
  function tallyInviteText(n, total) {
    return n
      ? `${n} of ${total} already — don’t lose count.`
      : `How many of the ${total} have you been to? Tap the ○ next to the ones you’ve visited — no account needed.`;
  }

  function tallyCtaText(n) {
    return n
      ? `Keep ${n === 1 ? 'this one' : `these ${n}`} — create a free account`
      : 'Start collecting';
  }

  /**
   * A tally tap patches in place — glyphs, group counts, bar, label and
   * both sentences. Never renderBody(): replacing the checklist DOM per
   * tap would close open groups and drop keyboard focus mid-count.
   */
  function patchTallyUi() {
    el.querySelectorAll('[data-tally-row]').forEach((row) => {
      const on = tally.has(row.dataset.tallyRow);
      row.classList.toggle('set-child-tallied', on);
      const mark = row.querySelector('[data-tally-cat]');
      if (mark) {
        mark.setAttribute('aria-pressed', String(on));
        const check = mark.querySelector('.set-child-check');
        if (check) check.textContent = on ? '✓' : '○';
      }
    });
    // Nested groups recount from the rows they contain (signed out, every
    // leaf in a group is a tally row, so rows.length IS the leaf count)
    el.querySelectorAll('.set-group').forEach((group) => {
      const groupRows = group.querySelectorAll('[data-tally-row]');
      if (!groupRows.length) return;
      const on = [...groupRows].filter((r) => tally.has(r.dataset.tallyRow)).length;
      const count = group.querySelector('.set-group-count');
      if (count) count.textContent = `${on}/${groupRows.length}`;
    });
    const panel = el.querySelector('.set-progress-empty');
    if (!panel) return;
    const { tree, total } = setTotals();
    const n = tallyCount(tree);
    const pct = total ? Math.round((n / total) * 100) : 0;
    const label = panel.querySelector('.set-progress-label');
    if (label) label.textContent = `${n}/${total} (${pct}%)`;
    const fill = panel.querySelector('.collection-fill');
    if (fill) fill.style.width = `${pct}%`;
    const invite = panel.querySelector('[data-tally-invite]');
    if (invite) invite.textContent = tallyInviteText(n, total);
    const cta = panel.querySelector('[data-tally-cta]');
    if (cta) cta.textContent = tallyCtaText(n);
  }

  function setPanelHtml() {
    const user = store.get().user;
    const { tree, total, done } = setTotals();
    const pct = total ? Math.round((done / total) * 100) : 0;
    const nodeHtml = (node) => {
      if (!node.kids.length) {
        if (!user) {
          // Signed out the row splits in two: the name still opens the
          // child page, and the circle is the tally toggle — the "have
          // you been here?" tap that needs no account.
          const tallied = tally.has(node.cat.id);
          return `
          <div class="set-child set-child-tally${tallied ? ' set-child-tallied' : ''}" data-tally-row="${node.cat.id}">
            <button type="button" class="set-child-open" data-child-slug="${categorySlug(node.cat)}">
              ${categoryIcon(node.cat, 18)}
              <span class="set-child-name">${escapeHtml(node.cat.name)}</span>
            </button>
            <button type="button" class="set-child-mark" data-tally-cat="${node.cat.id}"
              aria-pressed="${tallied}" aria-label="I’ve been to ${escapeHtml(node.cat.name)}">
              <span class="set-child-check" aria-hidden="true">${tallied ? '✓' : '○'}</span>
            </button>
          </div>`;
        }
        return `
          <button type="button" class="set-child${node.done ? ' set-child-done' : ''}"
            data-child-slug="${categorySlug(node.cat)}">
            ${categoryIcon(node.cat, 18)}
            <span class="set-child-name">${escapeHtml(node.cat.name)}</span>
            <span class="set-child-check" aria-hidden="true">${node.done ? '✓' : '○'}</span>
            <span class="visually-hidden">${node.done ? 'Collected' : 'Not yet collected'}</span>
          </button>`;
      }
      return `
        <details class="set-group${node.done ? ' set-child-done' : ''}">
          <summary aria-label="${escapeHtml(node.cat.name)}: ${node.doneLeaves} of ${node.leaves} collected — expands">
            ${categoryIcon(node.cat, 18)}
            <span class="set-child-name">${escapeHtml(node.cat.name)}</span>
            <span class="set-group-count">${node.doneLeaves}/${node.leaves}</span>
            <span class="set-child-check" aria-hidden="true">${node.done ? '✓' : '○'}</span>
            <span class="browse-caret" aria-hidden="true">▸</span>
          </summary>
          <div class="set-checklist set-group-children">${node.kids.map(nodeHtml).join('')}</div>
        </details>`;
    };
    // Signed in with a pre-signup tally still ahead of the real count:
    // the bridge from "I counted 12" to "log them so they count" — the
    // stamping run (./stamp.js) is the bulk path that makes that a few
    // minutes instead of twelve full log forms.
    const talliedBefore = user ? tallyCount(tree) : 0;
    const tallyBridge = user && talliedBefore > done
      ? `<p class="set-progress-invite">You counted ${talliedBefore} before joining — stamp them to make them count.</p>`
      : '';
    const stampDoor = user && total && done < total
      ? `<button type="button" class="v2-btn-more set-stamp-door" data-stamp-run>
           Been to several? Mark your visits →</button>`
      : '';
    const tallied = user ? 0 : tallyCount(tree);
    const tallyPct = total ? Math.round((tallied / total) * 100) : 0;
    return `
      ${user ? `
      <section class="set-progress" aria-label="Your progress">
        <div class="set-progress-head">
          <h3 class="friends-title">Your progress</h3>
          <span class="set-progress-label">${done}/${total} (${pct}%)</span>
        </div>
        <div class="collection-bar set-progress-bar"><span class="collection-fill" style="width:${pct}%"></span></div>
        ${pct === 100 ? '<p class="set-complete">Complete!</p>' : ''}
        ${tallyBridge}
        ${stampDoor}
      </section>` : `
      <!-- SIGNED OUT (2026-07-26): show the mechanic, do not describe it —
           and since the landing-pages push, let them WORK it. The bar is
           real and the circles are tappable: a visitor counts the ones
           they have been to, watches it fill, and the call to action
           offers to keep the count. The old version rendered honestly at
           zero; this one earns its percentage. -->
      <section class="set-progress set-progress-empty" aria-label="Progress through this collection">
        <div class="set-progress-head">
          <h3 class="friends-title">Your progress</h3>
          <span class="set-progress-label">${tallied}/${total} (${tallyPct}%)</span>
        </div>
        <div class="collection-bar set-progress-bar"><span class="collection-fill" style="width:${tallyPct}%"></span></div>
        <p class="set-progress-invite" data-tally-invite>${tallyInviteText(tallied, total)}</p>
        <button type="button" class="v2-btn-leather" data-open-auth data-tally-cta>${tallyCtaText(tallied)}</button>
      </section>`}
      <h3 class="logs-title">${escapeHtml(category.name)} <span class="geo-count">${total}</span></h3>
      <div class="set-checklist">
        ${tree.map(nodeHtml).join('')}
      </div>`;
  }

  /**
   * The empty category page, which until 2026-07-26 was a wrongly-centred
   * map and the sentence "Nothing yoinked in this category yet." — a dead
   * end with no verb on it. 51 of the 63 national parks land here, and it is
   * where a visitor arriving from a parks community with real intent ends up
   * the moment they tap a park nobody has logged.
   *
   * Three things, in the order they matter:
   *   1. a VERB. /log?category=<slug> already preselects the category
   *      (log.js:815), so the button drops you into the filled-in form.
   *      Signed out it opens the sign-in dialog instead, like the
   *      collection page's call to action.
   *   2. the SET CONTEXT — this park is one of 63, which is the mechanic
   *      the whole product leads with, and an empty leaf is exactly where
   *      it should be recruiting.
   *   3. PROOF OF LIFE — activity across the collection, so the page reads
   *      as early rather than abandoned.
   */
  function firstYoinkInviteHtml() {
    const parent = category.parent_id && allCatalog.find((c) => c.id === category.parent_id);
    const set = parent?.is_set ? parent : null;
    const signedIn = Boolean(store.get().user);

    let context = '';
    if (set) {
      const tree = buildSetTree(allCatalog, set.id, myYoinkedCatIds || new Set());
      const total = tree.reduce((n, node) => n + node.leaves, 0);
      const done = tree.reduce((n, node) => n + node.doneLeaves, 0);
      // The progress clause needs REAL collected ids. Until they land
      // (loadSetActivity fetches them for a leaf) the count would read 0 for
      // everyone, so the sentence stops at the collection's size.
      const known = signedIn && myYoinkedCatIds;
      if (total) {
        context = `<p class="empty-invite-context">
          ${escapeHtml(category.name)} is one of ${total} in
          ${escapeHtml(set.name)}${known ? ` — you have collected ${done}` : ''}.
        </p>`;
      }
    }

    // setActivity is null until the count lands, and stays null when there
    // is no set above this category — either way the line simply isn't there.
    const life = setActivity
      ? `<p class="empty-invite-life">${setActivity.label} across the collection so far.</p>`
      : '';

    return `
      <section class="empty-invite" aria-label="Be the first here">
        <h3 class="empty-invite-title">No one has yoinked ${escapeHtml(category.name)} yet.</h3>
        ${context}
        <button type="button" class="v2-btn-leather" data-first-yoink>
          Be the first${signedIn ? '' : ' — sign in to start'}
        </button>
        ${life}
      </section>`;
  }

  /** "Part of {Set} →" when the parent is a collection (V1 :915-927). */
  function setBacklinkHtml() {
    const parent = category.parent_id && allCatalog.find((c) => c.id === category.parent_id);
    if (!parent?.is_set) return '';
    return `
      <button type="button" class="v2-btn-more cat-set-backlink" data-child-slug="${categorySlug(parent)}">
        Part of ${escapeHtml(parent.name)} →</button>`;
  }

  /**
   * Child chips with V1's behaviors (category-panel.js:356-395,799-881):
   * unique-location counts from the loaded rows, count-sorted, empty
   * children hidden, tap filters in place, active re-tap navigates.
   */
  function childChipsHtml(sourceRows = rows) {
    if (!children.length || !sourceRows) return '';
    const counts = children.map((c) => {
      const ids = subtreeIds(c.id, allCatalog);
      const locs = new Set(
        sourceRows.filter((r) => ids.has(r.category_id || r.categories?.id)).map((r) => r.location_id).filter(Boolean)
      );
      return { cat: c, count: locs.size };
    }).filter((e) => e.count > 0).sort((a, b) => b.count - a.count);
    if (!counts.length) return '';
    const allLocs = new Set(sourceRows.map((r) => r.location_id).filter(Boolean)).size;
    return `
      <div class="v2-chips" role="group" aria-label="In this ${isSetPanel() ? 'collection' : 'category'}">
        <button class="v2-chip ${childFilter ? '' : 'chip-active'}" data-child-chip="">All (${allLocs})</button>
        ${counts.map(({ cat, count }) => `
          <button class="v2-chip ${childFilter === cat.id ? 'chip-active' : ''}" data-child-chip="${cat.id}"
            data-child-slug="${categorySlug(cat)}">
            ${categoryIcon(cat, 16)}<span>${escapeHtml(cat.name)} (${count})</span>
          </button>`).join('')}
      </div>`;
  }

  /** V1's "Uncollected Nearby" card (category-panel.js:1139-1211). */
  function nearbyHtml() {
    const me = store.get().user;
    if (!rows?.length) return '';
    if (!me) return '';
    if (!nearby) return '';
    if (nearby.state === 'denied') {
      return '<p class="log-hint">Turn on location access to see what’s still uncollected near you.</p>';
    }
    if (nearby.state === 'none') {
      return '<p class="log-hint">Nothing uncollected within 100km — this area is yours.</p>';
    }
    if (nearby.state !== 'ready' || !nearby.places.length) return '';
    return `
      <section class="journal-follows" aria-label="Uncollected near you">
        <h3 class="friends-title">Still uncollected near you</h3>
        <div class="collection-rows">
          ${nearby.places.map((loc) => `
            <button type="button" class="collection-row" data-nearby-loc="${loc.id}">
              <span class="collection-name">${escapeHtml(loc.display_name || 'A place')}</span>
              <span class="collection-progress">${loc.km < 1 ? 'under 1' : Math.round(loc.km)} km</span>
            </button>`).join('')}
        </div>
      </section>`;
  }

  /**
   * The lists shelf (20260813f): public lists whose owners filed them
   * under this category — "Top 16 Burgers in Los Angeles" on the Burgers
   * page. Any public list qualifies, not just featured ones (user
   * decision 2026-08-13: tagging a public list is publishing it into the
   * category); the ★ still marks the ranger picks.
   */
  function categoryListsHtml() {
    if (!catLists.length) return '';
    return `
      <section class="journal-follows" aria-label="Lists in this category">
        <h3 class="friends-title">Lists featuring ${escapeHtml(category.name)}</h3>
        <div class="mylist-rows">
          ${catLists.map((l) => `
            <button type="button" class="mylist-row" data-open-cat-list
              data-owner="${escapeHtml(l.owner.username)}" data-slug="${escapeHtml(l.slug)}">
              ${icon('bookmark', 20)}
              <span class="mylist-body">
                <span class="mylist-title">${l.featured ? '<span class="featured-star" aria-hidden="true">★</span> ' : ''}${escapeHtml(l.title)}</span>
                <span class="mylist-meta">by ${escapeHtml(l.owner.full_name || l.owner.username)} · ${l.entry_count} place${l.entry_count === 1 ? '' : 's'}</span>
                ${l.description ? `<span class="mylist-meta">${escapeHtml(l.description)}</span>` : ''}
              </span>
            </button>`).join('')}
        </div>
      </section>`;
  }

  function shownRows() {
    if (!rows) return [];
    const filterIds = childFilter ? subtreeIds(childFilter, allCatalog) : null;
    return filterIds
      ? rows.filter((r) => filterIds.has(r.category_id || r.categories?.id))
      : rows;
  }

  /** My collected location ids within these rows — greens the map pins. */
  function myCollectedHere() {
    const me = store.get().user;
    if (!me || !rows) return null;
    return new Set(rows.filter((r) => r.user_id === me.id).map((r) => r.location_id).filter(Boolean));
  }

  /**
   * The map's view carries into the cards (user report 2026-07-08, the
   * Zoltar zoom) — with the always-on map this is live: pan or zoom
   * and the grid follows. The coordful comparison keeps the "Showing X
   * of Y" line quiet at rest: the framed-to-pins viewport contains every
   * row that COULD be on the map, and rows without coordinates were
   * never on it, so they must not read as "filtered out".
   */
  function viewState() {
    const shown = shownRows();
    const coordful = shown.filter((r) =>
      typeof r.locations?.lat === 'number' && typeof r.locations?.lng === 'number');
    const areaRows = mapArea ? rowsInArea(shown, mapArea) : shown;
    const areaActive = Boolean(mapArea) && areaRows.length < coordful.length;
    return { shown, areaRows, areaActive };
  }

  /**
   * Child chips between the hero and the map. Chip counts follow the
   * map's viewport like V1's "in view" pill counts
   * (category-panel.js:1473-1508).
   */
  function controlsHtml() {
    if (isSetPanel() || rows === null) return '';
    const { areaActive } = viewState();
    const chipRows = areaActive ? rowsInArea(rows, mapArea) : rows;
    return childChipsHtml(chipRows);
  }

  function bodyHtml() {
    if (isSetPanel()) return setPanelHtml() + categoryListsHtml();
    if (rows === null) return '<div class="v2-loading">Unrolling the map…</div>';
    const { shown, areaRows, areaActive } = viewState();
    const list = areaActive ? areaRows : shown;
    return `
      ${setBacklinkHtml()}
      ${categoryListsHtml()}
      ${bragSection()}
      ${nearbyHtml()}
      ${areaActive ? `
      <p class="log-hint feed-area-line">Showing ${list.length} of ${shown.length} in the map’s view ·
        <button type="button" class="v2-search-clear" data-clear-map-area>Show everywhere</button>
      </p>` : ''}
      <div class="v2-grid">${list.slice(0, visibleCount).map(yoinkCard).join('')}</div>
      <div class="v2-feed-foot" aria-live="polite">
        ${list.length > visibleCount ? '<button type="button" class="v2-btn-more" data-show-more>Show more</button>' : ''}
        ${!list.length && (areaActive || rows.length)
          // Filtered to nothing — the category itself is not empty, so the
          // "be the first" invitation would be a lie. Say what happened.
          ? '<div class="v2-empty">Nothing here in this view.</div>'
          : ''}
        ${!list.length && !areaActive && !rows.length ? firstYoinkInviteHtml() : ''}
      </div>`;
  }

  /**
   * "About this collection" — the category's article (20260813), the
   * landing-page half of the SEO push. The crawler block in
   * inc/seo-content.php renders the SAME sections, and the no-cloaking rule
   * (CLAUDE.md) says it may only claim what the page visibly shows — this
   * section is that showing. Plain text in, escaped headings/paragraphs out;
   * anything malformed (or the column not yet applied) renders nothing.
   */
  function aboutHtml() {
    const sections = articleSections(article);
    if (!sections.length) return '';
    return `
      <section class="cat-about" aria-label="About ${escapeHtml(category.name)}">
        ${sections.map((s) => `
          ${s.heading ? `<h3 class="cat-about-heading">${escapeHtml(s.heading)}</h3>` : ''}
          ${s.paragraphs.map((p) => `<p class="cat-about-body">${articleParagraphHtml(p)}</p>`).join('')}
        `).join('')}
      </section>`;
  }

  async function loadArticle() {
    try {
      article = await fetchCategoryArticle(category.id);
      if (!destroyed && article) renderAbout();
    } catch { /* absence is the designed fallback — nothing to render */ }
  }

  function renderAbout() {
    if (aboutEl) aboutEl.innerHTML = aboutHtml();
  }

  function renderHero() {
    if (heroEl) heroEl.innerHTML = heroHtml();
  }

  function renderControls() {
    if (controlsEl) controlsEl.innerHTML = controlsHtml();
  }

  function renderBody() {
    if (bodyEl) bodyEl.innerHTML = bodyHtml();
  }

  /** The hint under the map follows the visitor — never a remount. */
  function updateMapChrome() {
    // Embed strip: the place count fills in when the rows land
    const embedCount = el.querySelector('[data-embed-count]');
    if (embedCount && rows) {
      const places = new Set(rows.map((r) => r.location_id).filter(Boolean)).size;
      embedCount.textContent = `${places} place${places === 1 ? '' : 's'} and counting`;
    }
    if (!mapHostEl) return;

    // A map with nothing on it does not just say nothing — it says something
    // WRONG. mountMap keeps store.mapCenter when there are no markers to
    // frame (map-view.js: fitToMarkers needs locs.length), so an unyoinked
    // category renders wherever the visitor last looked: Acadia NP over a
    // map of Calabasas (user report 2026-07-26). 51 of the 63 parks are in
    // this state. Hide the whole splash — the invitation below takes the
    // space and is the only thing on the page worth tapping.
    const splash = el.querySelector('.cat-map-splash');
    if (splash && rows) {
      splash.hidden = !hasMappableRows();
    }

    const hint = el.querySelector('[data-map-hint]');
    if (hint) {
      hint.textContent = store.get().user
        ? 'Green pins are yours already — orange ones are still out there.'
        : 'Every pin is a place explorers have logged in this category.';
    }
  }

  /** At least one row the map could actually draw. */
  function hasMappableRows() {
    return (rows || []).some((r) =>
      typeof r.locations?.lat === 'number' && typeof r.locations?.lng === 'number');
  }

  /**
   * Push the current rows into the always-on map. Signature-guarded:
   * setYoinks re-frames the board (fitToMarkers), so only a real change
   * — rows arriving, a chip filter, your collected set — may call it,
   * never store noise after a pan.
   */
  function syncMapPins() {
    if (!mapHandle || !rows || !mapsAvailable()) return;
    const shown = shownRows();
    const mine = myCollectedHere();
    const sig = `${shown.length}:${childFilter || ''}:${mine ? mine.size : -1}`;
    if (sig === pinsSig) return;
    pinsSig = sig;
    mapHandle.setYoinks(shown, mine);
  }

  function renderAll() {
    renderHero();
    renderControls();
    renderBody();
    renderAbout();
    updateMapChrome();
    syncMapPins();
  }

  /**
   * The one-time page skeleton. The map region is OUTSIDE every
   * re-rendered region, so the whole category stays on one board across
   * store noise, auth settling, chip taps, and the map's own idle
   * writes — remounting on those was the 2026-07-08 blink loop.
   */
  function buildShell() {
    mapHandle?.cleanup();
    mapHandle = null;
    if (embedded && !isSetPanel()) {
      // Embed frames are small (the snippet ships 480px), so the map IS
      // the embed (user call 2026-07-20: the splash hero forced scrolling
      // inside the frame). A one-line identity strip takes the hero's
      // place — the full splash is one attribution-bar tap away — and
      // the map fills the rest of the frame (CSS: .cat-embed-splash).
      el.innerHTML = `
        <div class="cat-embed-head">
          <span class="cat-head-icon" aria-hidden="true">${categoryIcon(category, 22)}</span>
          <strong>${escapeHtml(category.name)}</strong>
          <span class="cat-embed-count" data-embed-count aria-live="polite"></span>
        </div>
        <section class="cat-map-splash cat-embed-splash" aria-label="${escapeHtml(category.name)} location map">
          <div class="v2-map cat-splash-map" data-category-map></div>
        </section>`;
      heroEl = null;
      controlsEl = null;
      bodyEl = null;
      aboutEl = null;
      mapHostEl = el.querySelector('[data-category-map]');
      mapHandle = mountMap(mapHostEl, { store, router, fitToMarkers: true });
      renderAll();
      return;
    }
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back">
        <a href="${basePath}/" data-route="discover">← Discover</a>
        <span class="yoink-share-row">
          ${shareMenu(
            sharePhotosButton(),
            isSetPanel() ? '' : embedButton(category.name),
            shareButton(`${category.name} on Yoink Adventures`)
          )}
        </span>
      </nav>
      <div data-cat-hero></div>
      <div data-cat-controls></div>
      ${isSetPanel() ? '' : `
      <section class="cat-map-splash" aria-label="${escapeHtml(category.name)} location map">
        <div class="v2-map cat-splash-map" data-category-map></div>
        <p class="log-hint" data-map-hint></p>
      </section>`}
      <div data-cat-body></div>
      <div data-cat-about></div>`;
    heroEl = el.querySelector('[data-cat-hero]');
    controlsEl = el.querySelector('[data-cat-controls]');
    bodyEl = el.querySelector('[data-cat-body]');
    aboutEl = el.querySelector('[data-cat-about]');
    mapHostEl = el.querySelector('[data-category-map]');
    if (mapHostEl) {
      // The whole category on one board, framed to its pins, colored by
      // what you've collected (V1's category map treatment)
      mapHandle = mountMap(mapHostEl, {
        store, router, fitToMarkers: true,
        onBoundsChange: (b) => {
          mapArea = b;
          if (!destroyed && rows) {
            renderControls();
            renderBody();
          }
        },
      });
    }
    renderAll();
  }

  async function loadRows() {
    try {
      rows = await fetchSubtreeYoinks(category.id);
      if (destroyed) return;
      if (collagePhotos.length < 3) collagePhotos = heroPhotos(rows);
      renderAll();
      loadNearby();
      if (!rows.length) loadSetActivity(); // only an empty page asks
    } catch {
      if (destroyed) return;
      mapHandle?.cleanup();
      mapHandle = null;
      mapHostEl = null;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    }
  }

  /**
   * Proof of life for an empty page: how much has been logged across the
   * collection this category belongs to. The difference between "early" and
   * "abandoned" is evidence that anyone else is here.
   *
   * rawQuery has no exact-count mode, so this reads ids with a deliberate
   * cap and SAYS SO at the ceiling ("200+") rather than reporting a
   * truncated number as if it were the total — the silent-1000-row trap
   * this codebase keeps rediscovering.
   */
  async function loadSetActivity() {
    const parent = category.parent_id && allCatalog.find((c) => c.id === category.parent_id);
    if (!parent?.is_set) return;
    const ids = [...subtreeIds(parent.id, allCatalog)];
    if (!ids.length) return;
    const CAP = 200;

    // loadProgress() bails on a leaf (no children), so myYoinkedCatIds is
    // null here — and rendering "you have collected 0" to someone with 20
    // parks would be a lie the page states confidently. Fetch it for real.
    const me = store.get().user;
    if (me && !myYoinkedCatIds) {
      try {
        const mine = await fetchMyYoinkedCategoryIds(me.id);
        if (destroyed) return;
        myYoinkedCatIds = mine;
        renderBody();
      } catch { /* the clause simply stays off — see firstYoinkInviteHtml */ }
    }

    try {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
        select: 'id',
        filters: { category_id: `in.(${ids.join(',')})` },
        limit: CAP + 1,
      });
      if (error || destroyed || !data?.length) return;
      const n = data.length > CAP ? `${CAP}+` : `${data.length}`;
      setActivity = { label: `${n} yoink${data.length === 1 ? '' : 's'}` };
      renderBody();
    } catch {
      // decoration on an already-useful page — never block it
    }
  }

  function loadNearby() {
    const me = store.get().user;
    // Never inside an embed: the nearby card isn't shown there, and a
    // geolocation prompt from an iframe on someone else's site is rude
    if (embedded || !me || !rows?.length || nearby || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        markGeoGranted();
        if (destroyed) return;
        const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const myLocs = new Set(rows.filter((r) => r.user_id === me.id).map((r) => r.location_id).filter(Boolean));
        const places = uncollectedNearby(rows, myLocs, here);
        nearby = places.length ? { state: 'ready', places } : { state: 'none', places: [] };
        renderBody();
      },
      () => {
        if (destroyed) return;
        nearby = { state: 'denied', places: [] };
        renderBody();
      },
      { timeout: 8000, maximumAge: 300000 }
    );
  }

  // V1's Top Collectors card + the friendly comparison chip, framed
  // around the NEAREST rivals since 2026-07-19: "34 to catch the
  // leader" demotivates from #12, but "2 more and you pass Ben" is a
  // reachable dare — and "Cara is 1 behind you" is the defense version.
  // Rival rows and ranks come from the leaderboard-rivals RPC; on the
  // pre-rivals RPC (no rank column) those lines simply stay away.
  function bragSection() {
    if (!leaderboard.length) return '';
    const me = store.get().user;
    const { mine, top, above, below } = standing(leaderboard, me?.id);
    const top5 = leaderboard.slice(0, 5).map((u, i) => `
      <button type="button" class="collection-row" data-lb-username="${escapeHtml(u.username)}">
        <span class="collection-name">${u.rank ?? i + 1}. ${escapeHtml(u.name)}</span>
        <span class="collection-progress">${u.locationCount} place${u.locationCount === 1 ? '' : 's'}</span>
      </button>`).join('');
    // V1's "Your Rank" row (category-panel.js:1089-1113) when you're
    // outside the visible top 5 — the server's position when the RPC
    // carries ranks, else V1's client math over the loaded rows
    let rankLine = '';
    const visible = leaderboard.slice(0, 5);
    if (mine?.rank != null && !visible.some((u) => u.userId === mine.userId)) {
      rankLine = `<p class="log-hint">You’re #${mine.rank} — ${mine.locationCount} location${mine.locationCount === 1 ? '' : 's'}.</p>`;
    } else if (!mine?.rank && me && rows?.length) {
      const full = computeLeaderboard(rows);
      const idx = full.findIndex((u) => u.userId === me.id);
      if (idx >= 5) {
        rankLine = `<p class="log-hint">You’re #${idx + 1} — ${full[idx].locationCount} location${full[idx].locationCount === 1 ? '' : 's'}.</p>`;
      }
    }
    // Rival names tap through to their journals (user ask 2026-07-19,
    // iPad screenshot) — the same data-lb-username hop the top-5 rows
    // make, as an inline link inside the sentence. No username on the
    // row → plain text (nothing to open).
    const nameLink = (u) => (u.username
      ? `<button type="button" class="lb-name-link" data-lb-username="${escapeHtml(u.username)}">${escapeHtml(u.name)}</button>`
      : escapeHtml(u.name));
    const rivalLines = [];
    if (mine && above && above.userId !== top?.userId) {
      const gap = gapToPass(mine, above);
      rivalLines.push(`${gap} more place${gap === 1 ? '' : 's'} and you pass ${nameLink(above)} (#${above.rank}).`);
    }
    if (mine && below) {
      const diff = mine.locationCount - below.locationCount;
      rivalLines.push(diff === 0
        ? `${nameLink(below)} has caught up — you’re tied at ${mine.locationCount}.`
        : `${nameLink(below)} is ${diff} behind you.`);
    }
    return `
      <section class="journal-follows" aria-label="Top collectors">
        <h3 class="friends-title">Top collectors</h3>
        <div class="collection-rows">${top5}</div>
        ${rankLine}
        ${mine && top && top.userId !== mine.userId ? `
        <p class="log-hint">You: ${mine.locationCount} · Top: ${top.locationCount} — ${top.locationCount - mine.locationCount} to catch ${nameLink(top)}.</p>` : ''}
        ${mine && top && top.userId === mine.userId ? `
        <p class="log-hint">You’re the top collector here.</p>` : ''}
        ${rivalLines.map((line) => `<p class="log-hint cat-rival-line">${line}</p>`).join('')}
        ${mine && mine.locationCount > 0 ? `
        <button type="button" class="v2-btn-more cat-challenge" data-challenge>
          ${icon('share', 14)}<span data-share-label>Challenge a friend</span>
        </button>` : ''}
      </section>`;
  }

  async function loadStats() {
    try {
      const data = await fetchCategoryLeaderboard(category.id);
      if (destroyed) return;
      leaderboard = data;
      renderBody();
    } catch {
      // decoration — never block the page
    }
  }

  async function loadLists() {
    const lists = await fetchCategoryLists(category.id); // [] pre-migration / on failure
    if (destroyed || !lists.length) return;
    catLists = lists;
    renderBody();
  }

  async function loadProgress() {
    const user = store.get().user;
    if (!user || !children.length) return;
    try {
      const mine = await fetchMyYoinkedCategoryIds(user.id);
      if (destroyed) return;
      myYoinkedCatIds = mine;
      progress = computeProgress(children, mine);
      renderBody();
    } catch {
      // Progress is decoration on top of the page — never block it
    }
  }

  /**
   * The Set-header collage (V1 category-panel.js:610-645): up to 6 unique
   * first-images from recent yoinks across the children. Sets have no
   * subtree feed to derive photos from, so they keep this small query;
   * regular categories fill the collage from the rows they load anyway.
   */
  async function loadCollage() {
    if (!children.length) {
      collageReady = true; // nothing to fetch — don't strand Share photos
      return;
    }
    // Whole subtree, not just direct children — nested sets (Food Type)
    // keep their photos on the grandchildren (2026-07-12)
    const subtree = [];
    let frontier = children.map((c) => c.id);
    while (frontier.length) {
      subtree.push(...frontier);
      const parents = new Set(frontier);
      frontier = allCatalog.filter((c) => parents.has(c.parent_id)).map((c) => c.id);
    }
    try {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
        select: 'images',
        filters: { category_id: `in.(${subtree.join(',')})`, images: 'not.is.null' },
        order: 'created_at.desc',
        limit: 20,
      });
      if (error || destroyed) return;
      // Up to 10 for the share cards; the collage itself still shows 5
      // (collageHtml slices) and still needs 3 before it earns the space.
      const photos = heroPhotos(data || [], 10);
      setPhotos = photos;
      if (photos.length >= 3) {
        collagePhotos = photos;
        renderHero();
      }
    } catch {
      // the collage is decoration — never block the checklist
    } finally {
      collageReady = true;
    }
  }

  async function onFollowToggle() {
    if (followBusy || !category) return;
    followBusy = true;
    renderHero();
    try {
      if (isFollowingCategory(store, category.id)) {
        await unfollowCategory(store, category.id);
      } else {
        await followCategory(store, category.id);
      }
    } catch {
      // store unchanged; button re-renders in previous state
    }
    followBusy = false;
    if (!destroyed) renderHero();
  }

  // The category as a card set: its name over its freshest photos.
  // Everything comes from the already-loaded feed — no extra queries.
  const sharePhotos = createSharePhotosHandler(el, () => {
    if (!category) return null;

    // A COLLECTION has no subtree feed: mount() returns after
    // loadProgress/loadCollage for a Set, so `rows` stays null forever. The
    // source used to require an array there, and a null source is silently
    // swallowed by the handler — so Share photos was a dead button on every
    // Set page, including /category/national-park (user report 2026-07-26).
    // Its photos come from the collage query instead, and its meta line is
    // the collection's own number rather than a yoink count.
    if (isSetPanel()) {
      if (!collageReady) return null; // genuinely still loading — as the feed does
      const { total, done } = setTotals();
      return {
        baseName: `yoink-${categorySlug(category)}`,
        title: category.name,
        metaLine: total ? (done ? `${done} of ${total} collected` : `${total} to collect`) : '',
        byline: '',
        stampDate: null,
        category,
        photos: setPhotos.map((url) => thumbUrl(url)),
        hashtags: hashtags({ categories: category }),
      };
    }

    if (!Array.isArray(rows)) return null;
    // The same thumbUrl the feed grid displays — cached, so the cards
    // build inside the tap's activation window (cold originals don't)
    const photos = rows
      .map((y) => (Array.isArray(y.images) ? y.images.find(Boolean) : null))
      .filter(Boolean).slice(0, 10)
      .map((img) => thumbUrl(img.startsWith('http') ? img : gateway.getPublicUrl('yoinks', img)));
    return {
      baseName: `yoink-${categorySlug(category)}`,
      title: category.name,
      metaLine: rows.length ? `${rows.length} yoink${rows.length === 1 ? '' : 's'} and counting` : '',
      byline: '',
      stampDate: null,
      category,
      photos,
      hashtags: hashtags({ categories: category }),
    };
  });

  function onClick(event) {
    if (handleEmbedClick(event)) return;
    if (handleShareClick(event)) return;
    if (sharePhotos(event)) return;
    const tallyBtn = event.target.closest('[data-tally-cat]');
    if (tallyBtn) {
      const id = tallyBtn.dataset.tallyCat;
      if (tally.has(id)) tally.delete(id);
      else tally.add(id);
      saveTally();
      if (!tallyTracked) {
        // One event per visit says "this visitor engaged the tally";
        // the join event below carries the final count
        tallyTracked = true;
        track('collection_tally', { category: category?.name || null });
      }
      patchTallyUi();
      return;
    }
    if (event.target.closest('[data-stamp-run]')) {
      track('stamp_run_open', { category: category?.name || null });
      router.navigate('stamp', { slug: categorySlug(category) });
      return;
    }
    if (event.target.closest('[data-open-auth]')) {
      // The signed-out call to action. The header owns the dialog; this asks
      // for it by event so the view stays independent of the header's mount —
      // opened on the Create-account tab (this button recruits; the header's
      // own Sign in serves returning explorers). `tallied` measures whether
      // the tap-to-count mechanic is what converted them.
      track('visitor_cta', { action: 'join', view: 'category', tallied: tally.size });
      document.dispatchEvent(new CustomEvent(OPEN_AUTH_EVENT, { detail: { mode: 'signup' } }));
      return;
    }
    if (event.target.closest('[data-first-yoink]')) {
      // Signed out there is nothing to prefill yet — get them in first, and
      // the page they land back on is this one, still asking.
      if (!store.get().user) {
        document.dispatchEvent(new CustomEvent(OPEN_AUTH_EVENT));
        return;
      }
      router.navigate('log', {}, { query: { category: categorySlug(category) } });
      return;
    }
    if (event.target.closest('[data-follow-category]')) {
      onFollowToggle();
      return;
    }
    const challenge = event.target.closest('[data-challenge]');
    if (challenge) {
      const me = store.get().user;
      const mine = me ? leaderboard.find((u) => u.userId === me.id) : null;
      if (mine && category) {
        shareTextFrom(challenge, {
          title: `${category.name} on Yoink Adventures`,
          text: challengeText(category.name, mine),
        });
      }
      return;
    }
    const lb = event.target.closest('[data-lb-username]');
    if (lb && lb.dataset.lbUsername) {
      router.navigate('user', { username: lb.dataset.lbUsername });
      return;
    }
    if (event.target.closest('[data-clear-map-area]')) {
      mapArea = null;
      renderControls();
      renderBody();
      return;
    }
    const chip = event.target.closest('[data-child-chip]');
    if (chip) {
      const id = chip.dataset.childChip || null;
      if (id && id === childFilter) {
        // V1: tapping the already-active chip opens the child's page
        router.navigate('category', { slug: chip.dataset.childSlug });
        return;
      }
      childFilter = id;
      visibleCount = 48;
      renderControls();
      renderBody();
      syncMapPins(); // the board re-frames to the filtered pins
      return;
    }
    // A tap anywhere on a split tally row that missed the circle (handled
    // above) still opens the child — the row edges must not go dead just
    // because the row grew a second button.
    const child = event.target.closest('[data-child-slug]')
      || event.target.closest('[data-tally-row]')?.querySelector('[data-child-slug]');
    if (child) {
      router.navigate('category', { slug: child.dataset.childSlug });
      return;
    }
    const listRow = event.target.closest('[data-open-cat-list]');
    if (listRow) {
      router.navigate('list', { username: listRow.dataset.owner, slug: listRow.dataset.slug });
      return;
    }
    const nearbyRow = event.target.closest('[data-nearby-loc]');
    if (nearbyRow) {
      router.navigate('location', { id: nearbyRow.dataset.nearbyLoc });
      return;
    }
    if (event.target.closest('[data-show-more]')) {
      visibleCount += 48;
      renderBody();
      return;
    }
    const catRow = event.target.closest('[data-category-slug]');
    if (catRow) {
      router.navigate('category', { slug: catRow.dataset.categorySlug });
      return;
    }
    const userRow = event.target.closest('[data-username]');
    if (userRow && userRow.dataset.username) {
      router.navigate('user', { username: userRow.dataset.username });
      return;
    }
    const locationRow = event.target.closest('[data-location-id]');
    if (locationRow) {
      router.navigate('location', { id: locationRow.dataset.locationId });
      return;
    }
    const card = event.target.closest('[data-yoink-id]');
    if (card) router.navigate('yoink', { id: card.dataset.yoinkId });
  }

  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target.closest('[data-yoink-id], [data-location-id], [data-username], [data-category-slug]');
    if (!target || target.tagName === 'BUTTON') return;
    event.preventDefault();
    onClick(event);
  }

  el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';

  Promise.all([findCategoryBySlug(params.slug), fetchAllCategories()])
    .then(([found, all]) => {
      if (destroyed) return;
      category = found;
      if (!category) {
        el.innerHTML = `
          <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
          <div class="v2-empty">No category by that name.</div>`;
        return;
      }
      allCatalog = all;
      children = all.filter((c) => c.parent_id === category.id);
      document.title = `${category.name} | Yoink Adventures v2`;
      // The visitor ribbon's line: a collection is a challenge with a
      // number; a plain category is an invitation to count. The number is
      // the LEAF count across the whole subtree — the same total the
      // progress bar divides by. children.length here counted the direct
      // sub-groups (Food Type: "16" over a bar reading 0/134, user
      // screenshot 2026-07-27) — two different answers to one question.
      const setLeaves = isSetPanel() ? setTotals().total : 0;
      setRibbonContext(setLeaves
        ? `How many of the ${setLeaves} in ${category.name} have you been to? Make them count.`
        : `Been to any ${category.name} places? Log them — that’s a yoink.`);
      loadTally();
      buildShell(); // hero + map paint; the subtree feed fills in below
      if (!embedded) loadArticle(); // the embed shell has no About region
      if (isSetPanel()) {
        // V1 Set panels show the checklist, not a feed (no leaderboard)
        loadProgress();
        loadCollage();
        loadLists();
        return;
      }
      if (!embedded) {
        // Leaderboard, progress, nearby and the lists shelf are body
        // decorations the lean embed shell never shows — skip their
        // queries entirely
        loadStats();
        loadProgress();
        loadLists();
      }
      return loadRows();
    })
    .catch(() => {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    });

  // Follow state + login settle after render. Only identity-shaped
  // changes re-render — the map's own idle writes (mapCenter/mapZoom)
  // land in the same store, and re-rendering on those would rebuild the
  // hero on every pan for nothing.
  const relevantSig = () => {
    const s = store.get();
    return [s.user?.id || '', s.authReady, (s.followedCategoryIds || []).join(','), s.profile?.id || ''].join('|');
  };
  let lastSig = relevantSig();
  const unsubscribe = store.subscribe(() => {
    if (destroyed || !category) return;
    const sig = relevantSig();
    if (sig === lastSig) return;
    lastSig = sig;
    if (!progress) loadProgress();
    renderHero();
    renderBody();
    updateMapChrome();
    syncMapPins(); // your pins turn green the moment auth settles
  });

  el.addEventListener('click', onClick);
  el.addEventListener('keydown', onKeydown);

  return () => {
    destroyed = true;
    mapHandle?.cleanup();
    unsubscribe();
    el.removeEventListener('click', onClick);
    el.removeEventListener('keydown', onKeydown);
  };
}
